/**
 * The moderator's review inbox: join requests giftwrapped to them, local
 * review bookkeeping, and the approve/decline actions. Approval is a native
 * Concord Direct Invite (CORD-05 §6) — it opens in Armada too.
 */

import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNostr } from "@nostrify/react";

import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { useToast } from "@/hooks/useToast";
import { JOIN_REQUEST_KIND, MODERATOR_PUBKEY } from "@/lib/concord/config";
import {
  unwrapJoinRequest,
  createJoinResponseEvent,
  type ReceivedJoinRequest,
} from "@/lib/concord/requests";
import { bundleFromMembership, createDirectInvite } from "@/lib/concord/invites";
import { publishToInbox } from "@/lib/concord/inbox";
import type { Membership } from "@/lib/concord/types";

export interface ReviewedRecord {
  status: "approved" | "declined";
  at: number;
}

export interface InboxRequest extends ReceivedJoinRequest {
  reviewed?: ReviewedRecord;
}

export function useJoinRequests() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const isModerator = user?.pubkey === MODERATOR_PUBKEY;
  const [reviewed, setReviewed] = useLocalStorage<Record<string, ReviewedRecord>>(
    "chama:reviewed",
    {},
  );

  const query = useQuery({
    queryKey: ["chama", "requests", user?.pubkey],
    enabled: Boolean(isModerator && user?.signer.nip44),
    queryFn: async (c) => {
      const events = await nostr.query(
        [
          {
            kinds: [1059],
            "#p": [MODERATOR_PUBKEY],
            "#k": [String(JOIN_REQUEST_KIND)],
            limit: 100,
          },
        ],
        { signal: c.signal },
      );
      const out: ReceivedJoinRequest[] = [];
      for (const e of events) {
        const r = await unwrapJoinRequest(e, user!.signer);
        if (r) out.push(r);
      }
      out.sort((a, b) => b.createdAt - a.createdAt);
      return out;
    },
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const requests: InboxRequest[] = useMemo(
    () =>
      (query.data ?? []).map((r) => ({
        ...r,
        reviewed: reviewed[r.applicant],
      })),
    [query.data, reviewed],
  );

  const markReviewed = (applicant: string, status: ReviewedRecord["status"]) => {
    setReviewed((prev) => ({ ...prev, [applicant]: { status, at: Date.now() } }));
  };

  return {
    requests,
    isLoading: query.isLoading,
    refetch: query.refetch,
    markReviewed,
  };
}

export function useModerationActions(membership: Membership | undefined) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const approve = useMutation({
    mutationFn: async (applicantPub: string) => {
      if (!user || !membership) throw new Error("Not ready");
      const bundle = bundleFromMembership(membership, "Chama Chat");
      const wrap = await createDirectInvite({
        signer: user.signer,
        inviterPub: user.pubkey,
        recipientPub: applicantPub,
        bundle,
      });
      await publishToInbox(nostr, applicantPub, wrap);
    },
    onError: (e) => {
      console.error(e);
      toast({
        title: "Couldn't send the invite",
        description: "The invite failed to publish. Try again.",
        variant: "destructive",
      });
    },
  });

  const decline = useMutation({
    mutationFn: async (opts: { applicantPub: string; message: string }) => {
      if (!user) throw new Error("Not ready");
      const wrap = await createJoinResponseEvent({
        signer: user.signer,
        moderatorPub: user.pubkey,
        recipientPub: opts.applicantPub,
        message: opts.message,
      });
      await publishToInbox(nostr, opts.applicantPub, wrap);
    },
    onError: (e) => {
      console.error(e);
      toast({
        title: "Couldn't send the response",
        description: "The decline notice failed to publish. Try again.",
        variant: "destructive",
      });
    },
  });

  const invalidateControl = () =>
    queryClient.invalidateQueries({
      queryKey: ["chama", "control", membership?.communityId],
    });

  return { approve, decline, invalidateControl };
}
